import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';

import { useInlineCommentManager } from '../../contexts/InlineCommentManagerContext';
import { useToolbarActionService } from '../../contexts/ToolbarActionServiceContext';
import { ToolbarAction } from '../../services/ToolbarActionService';
import { ActionPanelId, ActionPanelState } from '../v2-content-action-panel/ActionPanelTypes';

/** The current action-panel state for a content page. */
export interface ActionPanelStateModel {
  /** Which panel is open, or null when collapsed. */
  readonly activePanel: ActionPanelState;
  /**
   * Configure what buttons to display in the toolbar.
   *
   * @param toolbarActions The header buttons to register. Pass a referentially
   *   stable value (e.g. a module constant or a `useMemo` result) so the actions
   *   are not re-registered on every render
   */
  readonly registerToolbarActions: (actions: readonly ToolbarAction[]) => void;
}

/**
 * Owns the right-side action-panel selection for a content page and registers
 * the given header toolbar actions.
 * Unregisters the actions on unmount. Shared by the content
 * editor and viewer pages.
 */
export function useActionPanelState(): ActionPanelStateModel {
  const toolbarService = useToolbarActionService();
  const inlineCommentManager = useInlineCommentManager();

  // when the toolbar service emits a change, then re-read the active panel
  const subscribeToToolbar = useCallback(
    (onStoreChange: () => void) => toolbarService.on('changed', onStoreChange),
    [toolbarService],
  );
  const activePanel = useSyncExternalStore(subscribeToToolbar, () => toolbarService.activePanel);

  // when the inline comment manager requests a sidebar reveal, then open the comments panel
  useEffect(() => {
    return inlineCommentManager.on('revealInSidebarRequested', () => {
      toolbarService.activePanel = 'comments';
    });
  }, [inlineCommentManager, toolbarService]);

  const [toolbarActions, setToolbarActions] = useState<readonly ToolbarAction[] | undefined>(
    undefined,
  );
  const toolbarActionsRef = useRef(toolbarActions);
  toolbarActionsRef.current = toolbarActions;
  const registrationIdRef = useRef(0);

  // when toolbar actions change, then register them with the service
  useEffect(() => {
    if (toolbarActions) {
      registrationIdRef.current = toolbarService.registerActions(toolbarActions);
    }
  }, [toolbarService, toolbarActions]);

  // when the view unmounts, then only clear if the service still holds this view's actions
  useEffect(() => {
    return () => {
      toolbarService.tryDeregisterActions(registrationIdRef.current);
    };
  }, [toolbarActions, toolbarService]);

  const registerToolbarActions = useCallback((actions: readonly ToolbarAction[]) => {
    // To make registerToolbarActions() safe to call inside react rendering loop,
    // don't do anything if the exact actions already registered.
    // This prevents infinitive render loop.
    if (toolbarActionsRef.current === actions) return;
    setToolbarActions(actions);
  }, []);

  return {
    activePanel,
    registerToolbarActions,
  };
}
